<template>
  <div>
    <div class="application__title">
      <h2>{{ paymentContract.name }}</h2>
    </div>
    <p class="application__subtitle">Select payment contract options</p>

    <PaymentContractOption
      v-for="(option, index) in paymentContract.options"
      :key="index"
      :selected-option="selectedOption"
      :option="option"
      :order="index + 1"
      :disabled="disabledPaymentContract"
      :total-amount-for-pay="getAmountForOption(option)"
      @selectOption="onSelectOption"
      @selectMethod="selectMethod"
      @optionValidation="selectedOptionValidation"
      @updateStartDate="updateOptionStartDate"
      @updateAmountOfPayments="updateOptionAmountOfPayments"
      @updateCustomSchedule="updateOptionCustomSchedule"
    />

    <!-- Scheduled Payments -->
    <SelectedOptionDetails
      v-if="userSelectedOptionDetails.paymentMethod"
      :userSelectedOptionDetails="userSelectedOptionDetails"
      :scheduled-payments="scheduledPayments"
      :total-amount-for-pay="totalAmountForPay"
      :applicable-fees="applicableFees"
    />

    <!-- Payment Details -->
    <PaymentBreakdown
      v-if="selectedOption"
      :sessions="selectedSessions"
      :groups="groupsForPCComputed"
      :products="productPurchasesForPCComputed"
      :paid-amount="paidAmountForPc"
      :hide-price-breakdown="hidePriceBreakdown"
      :discounts="selectedDiscounts"
      :additional-charge="additionalCharge"
      :additional-charge-name="additionalChargeName"
      :total-amount-for-pay="totalAmountForPay"
      :applicable-fees="applicableFees"
    />

    <p class="form__error" >{{ errorMessage }}</p>
  </div>
</template>

<script>
import Vue from 'vue'
import PaymentContractOption from '@/components/site/payment-contract/PaymentContractOption.vue'
import PaymentBreakdown from '@/components/site/payment-contract/PaymentBreakdown.vue'
import SelectedOptionDetails from '@/components/site/payment-contract/SelectedOptionDetails.vue'
import store from '@/store'
import { mapGetters } from 'vuex'
import {
  calculateDiscount,
  calculatePaymentSums,
  calculateTotalAmount,
  calculateApplicableFees,
} from '@/utils/payment'
import { preparePaymentOptionPayload } from '@/utils/preparePaymentOptionPayload'

export default {
  components: {
    PaymentContractOption,
    PaymentBreakdown,
    SelectedOptionDetails,
  },
  props: {
    hidePriceBreakdown: {
      type: Boolean,
      default: false,
    },
    stepStatus: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedOption: null,
      userSelectedOptionDetails: {
        paymentMethod: null,
        isChargeImmediately: null,
      },
      scheduledPayments: [],
      errorMessage: '',
      debounceTimeout: null,
      isLoading: false,
    }
  },
  watch: {
    paymentContract(val) {
      if (val.selectedOption) this.selectedOption = val.selectedOption
    },
  },
  async mounted() {
    this.prepareAutoAssignedGroups()
    // Fetch payment fees for this program
    if (this.programId) {
      await store.dispatch('applicationRegistration/fetchPaymentFees', this.programId)
    }
  },
  computed: {
    ...mapGetters({
      applicationId: 'applicationRegistration/getApplicationId',
      programId:     'applicationRegistration/getProgramID',
      selectedSessions: 'applicationRegistration/getSelectedSessionsList',
      paymentContractIsExist: 'applicationRegistration/getPaymentContractIsExist',
      paymentContract: 'applicationRegistration/getPaymentContract',
      selectedGroupsForPC: 'applicationRegistration/getSelectedGroupsForPC',
      groupsForAutoAssign: 'applicationRegistration/getGroupsForAutoAssign',
      productPurchases: 'applicationRegistration/getProductPurchases',
      paidAmountForPc: 'applicationRegistration/getPaidAmountForPc',
      paymentFees:     'applicationRegistration/getPaymentFees',
    }),
    groupsForPCComputed() {
      const groupsMap = new Map()

      this.selectedGroupsForPC
        .filter(group => group.price != 0)
        .forEach(group => groupsMap.set(group.id, group))

      this.groupsForAutoAssign
        .filter(group => group.price != 0)
        .forEach(group => {
          if (!groupsMap.has(group.id)) {
            groupsMap.set(group.id, group)
          }
        })

      return Array.from(groupsMap.values())
    },
    productPurchasesForPCComputed() {
      return this.productPurchases.filter(p => p.is_in_pc && p.amount != 0)
    },
    paymentSums() {
      return calculatePaymentSums({
        sessions: this.selectedSessions,
        groups: this.groupsForPCComputed,
        products: this.productPurchasesForPCComputed,
        paidAmount: this.paidAmountForPc,
        additionalCharge: this.additionalCharge,
      })
    },
    /** Base total before fees */
    totalBeforeFees() {
      return calculateTotalAmount({
        discountableSum: this.paymentSums.discountableSum,
        nonDiscountableSum: this.paymentSums.nonDiscountableSum,
        discount: this.discount,
      })
    },
    /**
     * Active charge-type contexts for this payment contract page.
     * 'PaymentContract' is always included.
     * 'Session', 'Group', 'Product' are added only when the relevant items exist.
     */
    activeChargeTypeContexts() {
      const contexts = ['PaymentContract']
      if (this.selectedSessions.length > 0) contexts.push('Session')
      if (this.groupsForPCComputed.length > 0) contexts.push('Group')
      if (this.productPurchasesForPCComputed.length > 0) contexts.push('Product')
      return contexts
    },
    /** Fees that match the currently selected payment method */
    applicableFees() {
      const method = this.userSelectedOptionDetails.paymentMethod
      return calculateApplicableFees(this.paymentFees, method, this.totalBeforeFees, this.activeChargeTypeContexts)
    },
    /** Total fee amount to add on top */
    totalFeeAmount() {
      return this.applicableFees.reduce((sum, f) => sum + f.calculatedAmount, 0)
    },
    /** Grand total including fees */
    totalAmountForPay() {
      return this.totalBeforeFees + this.totalFeeAmount
    },
    selectedOptionObject() {
      return this.paymentContract.options.find(o => o.id === this.selectedOption)
    },
    discount() {
      return calculateDiscount(
        this.paymentSums.discountableSum,
        this.selectedOptionObject
      )
    },
    selectedDiscounts() {
      if (!this.selectedOptionObject) return []
      return this.selectedOptionObject.price_option?.discounts ?? []
    },
    additionalCharge() {
      if (!this.selectedOptionObject) return 0
      const po = this.selectedOptionObject.price_option
      return po.priceType === 'Full Price + Additional Charge'
        ? Number(po.additionalCharge)
        : 0
    },
    additionalChargeName() {
      if (!this.selectedOptionObject) return 'Additional Charge'
      const po = this.selectedOptionObject.price_option
      return po.priceType === 'Full Price + Additional Charge' && po.chargeName
        ? po.chargeName
        : 'Additional Charge'
    },
    disabledPaymentContract() {
      return !!(this.paymentContractIsExist && this.stepStatus) || this.paymentContract.options.some(item => item.defaults)
    },
  },
  methods: {
    debouncedFetchFuturePayments() {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout)

      this.debounceTimeout = setTimeout(() => {
        this.fetchFuturePayments()
      }, 300)
    },
    getAmountForOption(option) {
      const po = option.price_option
      const additionalCharge = po.priceType === 'Full Price + Additional Charge'
        ? Number(po.additionalCharge)
        : 0

      const { discountableSum, nonDiscountableSum } = calculatePaymentSums({
        sessions: this.selectedSessions,
        groups: this.groupsForPCComputed,
        products: this.productPurchasesForPCComputed,
        paidAmount: this.paidAmountForPc,
        additionalCharge,
      })

      const discount = calculateDiscount(discountableSum, option)
      const baseAmount = calculateTotalAmount({ discountableSum, nonDiscountableSum, discount })

      // Add fees for this specific option's method
      const method = option.selectedMethod
      const fees = calculateApplicableFees(this.paymentFees, method, baseAmount, this.activeChargeTypeContexts)
      const feeTotal = fees.reduce((sum, f) => sum + f.calculatedAmount, 0)

      return baseAmount + feeTotal
    },
    onSelectOption(option) {
      this.selectedOption = option.id
      this.paymentContract.selectedOption = option.id
      this.selectMethod(option.selectedMethod)
      this.debouncedFetchFuturePayments()
    },
    selectMethod(val) {
      this.$emit('viewableCC', val !== 'Credit Card')
      this.$emit('viewableACH', val !== 'ACH')
      this.debouncedFetchFuturePayments()
    },
    updateOptionStartDate({ optionId, startDate }) {
      const option = this.paymentContract.options.find(o => o.id === optionId)
      if (option) option.startDate = startDate

      this.debouncedFetchFuturePayments()
    },
    updateOptionAmountOfPayments({ optionId, amount }) {
      const option = this.paymentContract.options.find(o => o.id === optionId)
      if (option) option.amountOfPayments = amount

      this.debouncedFetchFuturePayments()
    },
    updateOptionCustomSchedule({ optionId, customSchedule }) {
      const option = this.paymentContract.options.find(o => o.id === optionId)
      if (option) option.customSchedule = customSchedule

      this.debouncedFetchFuturePayments()
    },
    selectedOptionValidation(data) {
      if (this.selectedOption === data.id) {
        Vue.set(this.paymentContract, 'optionInvalid', data.hasError)
      }
    },
    async prepareAutoAssignedGroups() {
      if (!this.applicationId) return
      await store.dispatch('applicationRegistration/fetchAutoAssignedGroupsList', {
        application_id: this.applicationId,
      })
    },
    async fetchFuturePayments() {
      this.scheduledPayments = []
      this.errorMessage = ''
      const option = this.paymentContract.options.find(option => option.id === this.paymentContract.selectedOption)

      if (!option) return
      if (!this.applicationId) return

      this.userSelectedOptionDetails = {
        isChargeImmediately: option.schedule_option.timeSchedule == 'Charge Immediately',
        paymentMethod: option.selectedMethod,
        amountOfPayments: option.amountOfPayments,
      }

      if (this.userSelectedOptionDetails.isChargeImmediately) {
        return
      }

      // ── Custom schedule ──
      if (option.schedule_option.allowCustomSchedule) {
        const schedule = option.customSchedule ?? []
        const allFilled = schedule.length > 0
          && schedule.every(r => r.date && parseFloat(r.amount) > 0)
        if (allFilled) {
          this.scheduledPayments = schedule.map(r => ({
            date: r.date,
            amount: parseFloat(r.amount),
          }))
        }
        return
      }

      // ── Standard schedule ──
      if (!option.startDate) return

      let payload = preparePaymentOptionPayload(
        this.applicationId,
        this.paymentContract.selectedOption,
        option,
      )
      payload.total_amount_for_pay = this.totalAmountForPay

      this.isLoading = true
      try {
        const response = await store.dispatch('applicationRegistration/fetchScheduledPayments', payload)
        this.scheduledPayments = response
      } catch (err) {
        console.error(err)
        this.errorMessage = 'Failed to fetch scheduled payments'
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
